/**
 * API client for Rancher, Vault, and Kubeconfig integrations
 */

import axios, { AxiosInstance } from 'axios';

// Create axios instance for configuration endpoints with authentication
const configApi: AxiosInstance = axios.create({
  baseURL: '/api/integrations',
  timeout: 10000,
});

// Add authentication interceptor that safely handles Keycloak
// This uses a synchronous check to avoid hanging on dynamic imports
configApi.interceptors.request.use((config) => {
  // Check if Keycloak auth token is available in window (set by main app)
  // This avoids any async operations or module imports that could hang
  const token = (window as any).__keycloakToken__;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// ============================================================================
// Types
// ============================================================================

export interface RancherConfig {
  id: string;                   // Instance ID
  name: string;                 // Instance display name
  rancherUrl: string;
  username?: string;
  token?: string;
  clusters: RancherCluster[];
  lastAuthenticated?: string;
}

export interface RancherCluster {
  id: string;
  name: string;
  enabled: boolean;
  kubeconfigGenerated: boolean;
  kubeconfigPath?: string;
  lastUpdated?: string;
}

export interface RancherAuthRequest {
  instanceId?: string;          // Optional: specify ID for existing instance
  instanceName?: string;        // Optional: friendly name for new instance
  rancherUrl: string;
  username: string;
  password: string;
}

export interface RancherAuthResponse {
  token: string;
  userId: string;
  clusters: RancherClusterInfo[];
}

export interface RancherClusterInfo {
  id: string;
  name: string;
  state: string;
  provider: string;
}

export interface VaultConfig {
  id: string;                   // Instance ID
  name: string;                 // Instance display name
  vaultUrl: string;
  authMethod: 'token' | 'approle' | 'aws' | 'kubernetes' | 'oidc';
  token?: string;
  roleId?: string;
  secretId?: string;
  oidcRole?: string;
  awsSecretsEngine: VaultAWSSecretsEngine;
  kvPaths: VaultKVPath[];       // KV (Key-Value) secrets paths
  lastAuthenticated?: string;
  vaultClientToken?: string;
  vaultTokenExpiration?: string;
}

export interface VaultAWSSecretsEngine {
  mountPoint: string;
  paths: VaultAWSPath[];
}

export interface VaultAWSPath {
  id: string;
  name: string;
  vaultInstanceId: string;      // Reference to VaultConfig.id
  mountPoint: string;
  path: string;
  type: 'sts' | 'iam';
  ttl?: number;
  enabled: boolean;
}

export interface VaultKVPath {
  id: string;
  name: string;
  vaultInstanceId: string;      // Reference to VaultConfig.id
  mountPoint: string;
  path: string;
  version?: number;             // KV version (1 or 2), defaults to 2
  enabled: boolean;
}

export interface VaultKVSecret {
  data: Record<string, any>;
  metadata?: {
    version: number;
    createdTime: string;
    deletionTime?: string;
    destroyed: boolean;
  };
}

export interface VaultAuthRequest {
  instanceId?: string;          // Optional: specify ID for existing instance
  instanceName?: string;        // Optional: friendly name for new instance
  vaultUrl: string;
  authMethod: 'token' | 'approle' | 'aws' | 'kubernetes' | 'oidc';
  token?: string;
  roleId?: string;
  secretId?: string;
  oidcRole?: string;
  awsMountPoint?: string;
}

export interface VaultAWSCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  expiresAt: string;
  renewable: boolean;
}

export interface VaultAWSRoleInfo {
  name: string;
  mountPoint: string;
  credentialType: 'iam_user' | 'assumed_role' | 'federation_token';
  path: string;
}

export interface KubeconfigMetadata {
  id: string;
  name: string;
  source: 'manual' | 'rancher' | 'eks' | 'gke' | 'aks';
  filePath: string;
  rancherInstanceId?: string;
  rancherClusterId?: string;
  rancherClusterName?: string;
  eksClusterName?: string;
  eksRegion?: string;
  contexts: KubeconfigContext[];
  currentContext?: string;
  createdAt: string;
  updatedAt: string;
}

export interface KubeconfigContext {
  name: string;
  cluster: string;
  user: string;
  namespace?: string;
}

export interface KubeconfigUploadRequest {
  name: string;
  kubeconfigContent: string;
  setAsDefault?: boolean;
}

export interface KubeconfigValidationResult {
  valid: boolean;
  errors: string[];
  contexts: KubeconfigContext[];
  clusters: string[];
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  details?: any;
}

export interface EKSClusterInfo {
  name: string;
  arn: string;
  region: string;
  status: string;
  version: string;
  endpoint: string;
  createdAt: string;
}

export interface EKSKubeconfigRequest {
  clusterName: string;
  region: string;
  vaultPathId: string;
  roleArn?: string;
}

export type KeycloakAuthFlow = 'client_credentials' | 'authorization_code';

export interface KeycloakConfig {
  id: string;
  name: string;
  url: string;
  realm: string;
  clientId: string;
  clientSecret?: string;
  authFlow: KeycloakAuthFlow;
  redirectUri?: string;
  scopes?: string[];
  tokenEndpoint?: string;
  authEndpoint?: string;
  accessToken?: string;
  refreshToken?: string;
  tokenExpiration?: string;
  lastAuthenticated?: string;
}

export interface KeycloakAuthRequest {
  instanceId?: string;
  instanceName?: string;
  url: string;
  realm: string;
  clientId: string;
  clientSecret?: string;
  authFlow: KeycloakAuthFlow;
  redirectUri?: string;
  scopes?: string[];
  authorizationCode?: string;
}

export interface KeycloakIntrospectResponse {
  active: boolean;
  exp?: number;
  iat?: number;
  client_id?: string;
  username?: string;
  scope?: string;
}

export interface RangeOSEnvironment {
  id: string;
  name: string;
  url: string;
  keycloakInstanceId?: string;
  apiBasePath?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Rancher API
// ============================================================================

export const rancherApi = {
  /**
   * Get all Rancher instances
   */
  getAllInstances: async (): Promise<RancherConfig[]> => {
    const { data } = await configApi.get<RancherConfig[]>('/ranchers');
    return data;
  },

  /**
   * Get specific Rancher instance
   */
  getInstance: async (instanceId: string): Promise<RancherConfig> => {
    const { data } = await configApi.get<RancherConfig>(`/ranchers/${instanceId}`);
    return data;
  },

  /**
   * Update a Rancher instance
   */
  updateInstance: async (instanceId: string, updates: Partial<RancherConfig>): Promise<RancherConfig> => {
    const { data } = await configApi.put<{ success: boolean; instance: RancherConfig }>(`/ranchers/${instanceId}`, updates);
    return data.instance;
  },

  /**
   * Delete Rancher instance
   */
  deleteInstance: async (instanceId: string): Promise<void> => {
    await configApi.delete(`/ranchers/${instanceId}`);
  },

  /**
   * Authenticate with Rancher
   */
  authenticate: async (request: RancherAuthRequest): Promise<RancherAuthResponse> => {
    const { data } = await configApi.post<{ success: boolean; data: RancherAuthResponse }>('/rancher/auth', request);
    return data.data;
  },

  /**
   * Get Rancher configuration (legacy - returns first instance)
   * @deprecated Use getAllInstances() or getInstance() instead
   */
  getConfig: async (): Promise<RancherConfig | null> => {
    const { data } = await configApi.get<RancherConfig | null>('/rancher/config');
    return data;
  },

  /**
   * Refresh cluster list from Rancher
   */
  refreshClusters: async (): Promise<RancherCluster[]> => {
    const { data } = await configApi.post<{ success: boolean; clusters: RancherCluster[] }>('/rancher/refresh-clusters');
    return data.clusters;
  },

  /**
   * Enable/disable a cluster
   */
  toggleCluster: async (clusterId: string, enabled: boolean): Promise<void> => {
    await configApi.post(`/rancher/clusters/${clusterId}/toggle`, { enabled });
  },

  /**
   * Generate kubeconfig for a cluster
   */
  generateKubeconfig: async (clusterId: string): Promise<string> => {
    const { data } = await configApi.post<{ success: boolean; kubeconfigPath: string }>(
      `/rancher/clusters/${clusterId}/generate-kubeconfig`
    );
    return data.kubeconfigPath;
  },

  /**
   * Test Rancher connection
   */
  testConnection: async (rancherUrl: string, token?: string, username?: string, password?: string): Promise<TestConnectionResponse> => {
    const { data } = await configApi.post<TestConnectionResponse>('/rancher/test', { rancherUrl, token, username, password });
    return data;
  },

  /**
   * Remove Rancher configuration
   */
  removeConfig: async (): Promise<void> => {
    await configApi.delete('/rancher/config');
  },
};

// ============================================================================
// Vault API
// ============================================================================

export const vaultApi = {
  /**
   * Get all Vault instances
   */
  getAllInstances: async (): Promise<VaultConfig[]> => {
    const { data } = await configApi.get<VaultConfig[]>('/vaults');
    return data;
  },

  /**
   * Get specific Vault instance
   */
  getInstance: async (instanceId: string): Promise<VaultConfig> => {
    const { data } = await configApi.get<VaultConfig>(`/vaults/${instanceId}`);
    return data;
  },

  /**
   * Update a Vault instance
   */
  updateInstance: async (instanceId: string, updates: Partial<VaultConfig>): Promise<VaultConfig> => {
    const { data } = await configApi.put<{ success: boolean; instance: VaultConfig }>(`/vaults/${instanceId}`, updates);
    return data.instance;
  },

  /**
   * Delete Vault instance
   */
  deleteInstance: async (instanceId: string): Promise<void> => {
    await configApi.delete(`/vaults/${instanceId}`);
  },

  /**
   * Authenticate with Vault
   */
  authenticate: async (request: VaultAuthRequest): Promise<void> => {
    await configApi.post('/vault/auth', request);
  },

  /**
   * Get Vault configuration (legacy - returns first instance)
   * @deprecated Use getAllInstances() or getInstance() instead
   */
  getConfig: async (): Promise<VaultConfig | null> => {
    const { data } = await configApi.get<VaultConfig | null>('/vault/config');
    return data;
  },

  /**
   * Add AWS secrets path
   */
  addAWSPath: async (path: Omit<VaultAWSPath, 'id'>): Promise<VaultAWSPath> => {
    const { data } = await configApi.post<{ success: boolean; data: VaultAWSPath }>('/vault/aws-paths', path);
    return data.data;
  },

  /**
   * Remove AWS secrets path
   */
  removeAWSPath: async (pathId: string): Promise<void> => {
    await configApi.delete(`/vault/aws-paths/${pathId}`);
  },

  /**
   * Toggle AWS path enabled/disabled
   */
  toggleAWSPath: async (pathId: string, enabled: boolean): Promise<void> => {
    await configApi.post(`/vault/aws-paths/${pathId}/toggle`, { enabled });
  },

  /**
   * Get AWS credentials from Vault
   */
  getAWSCredentials: async (pathId: string, forceRefresh: boolean = false): Promise<VaultAWSCredentials> => {
    const { data } = await configApi.get<{ success: boolean; data: VaultAWSCredentials }>(
      `/vault/aws-paths/${pathId}/credentials`,
      { params: { forceRefresh } }
    );
    return data.data;
  },

  /**
   * Renew Vault token
   */
  renewToken: async (): Promise<void> => {
    await configApi.post('/vault/renew-token');
  },

  /**
   * Test Vault connection
   */
  testConnection: async (vaultUrl: string, token?: string): Promise<TestConnectionResponse> => {
    const { data } = await configApi.post<TestConnectionResponse>('/vault/test', { vaultUrl, token });
    return data;
  },

  /**
   * Clear credential cache
   */
  clearCache: async (): Promise<void> => {
    await configApi.post('/vault/clear-cache');
  },

  /**
   * Remove Vault configuration
   */
  removeConfig: async (): Promise<void> => {
    await configApi.delete('/vault/config');
  },

  /**
   * Discover available AWS roles from Vault
   */
  discoverRoles: async (): Promise<{ stsRoles: VaultAWSRoleInfo[]; iamRoles: VaultAWSRoleInfo[] }> => {
    const { data } = await configApi.get<{ success: boolean; data: { stsRoles: VaultAWSRoleInfo[]; iamRoles: VaultAWSRoleInfo[] } }>('/vault/discover-roles');
    return data.data;
  },

  /**
   * Add KV secrets path
   */
  addKVPath: async (instanceId: string, path: Omit<VaultKVPath, 'id' | 'vaultInstanceId'>): Promise<VaultKVPath> => {
    const { data } = await configApi.post<{ success: boolean; data: VaultKVPath }>(`/vaults/${instanceId}/kv-paths`, path);
    return data.data;
  },

  /**
   * Remove KV secrets path
   */
  removeKVPath: async (pathId: string): Promise<void> => {
    await configApi.delete(`/vault/kv-paths/${pathId}`);
  },

  /**
   * Toggle KV path enabled/disabled
   */
  toggleKVPath: async (pathId: string, enabled: boolean): Promise<void> => {
    await configApi.post(`/vault/kv-paths/${pathId}/toggle`, { enabled });
  },

  /**
   * Read KV secret data
   */
  readKVSecret: async (pathId: string): Promise<VaultKVSecret> => {
    const { data } = await configApi.get<{ success: boolean; data: VaultKVSecret }>(`/vault/kv-paths/${pathId}/secret`);
    return data.data;
  },
};

// ============================================================================
// Kubeconfig API
// ============================================================================

export const kubeconfigApi = {
  /**
   * Get all kubeconfigs
   */
  list: async (): Promise<KubeconfigMetadata[]> => {
    const { data } = await configApi.get<KubeconfigMetadata[]>('/kubeconfigs');
    return data;
  },

  /**
   * Get kubeconfig by ID
   */
  get: async (id: string): Promise<KubeconfigMetadata> => {
    const { data } = await configApi.get<KubeconfigMetadata>(`/kubeconfigs/${id}`);
    return data;
  },

  /**
   * Get kubeconfig content
   */
  getContent: async (id: string): Promise<string> => {
    const { data } = await configApi.get<{ content: string }>(`/kubeconfigs/${id}/content`);
    return data.content;
  },

  /**
   * Upload kubeconfig
   */
  upload: async (request: KubeconfigUploadRequest): Promise<KubeconfigMetadata> => {
    const { data } = await configApi.post<{ success: boolean; data: KubeconfigMetadata }>('/kubeconfigs', request);
    return data.data;
  },

  /**
   * Validate kubeconfig
   */
  validate: async (kubeconfigContent: string): Promise<KubeconfigValidationResult> => {
    const { data } = await configApi.post<KubeconfigValidationResult>('/kubeconfigs/validate', { kubeconfigContent });
    return data;
  },

  /**
   * Update kubeconfig
   */
  update: async (id: string, updates: Partial<KubeconfigMetadata>): Promise<KubeconfigMetadata> => {
    const { data } = await configApi.put<{ success: boolean; data: KubeconfigMetadata }>(`/kubeconfigs/${id}`, updates);
    return data.data;
  },

  /**
   * Delete kubeconfig
   */
  delete: async (id: string): Promise<void> => {
    await configApi.delete(`/kubeconfigs/${id}`);
  },

  /**
   * Test kubeconfig
   */
  test: async (id: string, context?: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await configApi.post<{ success: boolean; message: string }>(`/kubeconfigs/${id}/test`, { context });
    return data;
  },

  /**
   * Refresh kubeconfig
   */
  refresh: async (id: string): Promise<KubeconfigMetadata> => {
    const { data } = await configApi.post<{ success: boolean; data: KubeconfigMetadata }>(`/kubeconfigs/${id}/refresh`);
    return data.data;
  },
};

// ============================================================================
// EKS API
// ============================================================================

export const eksApi = {
  /**
   * Discover EKS clusters using Vault AWS credentials
   */
  discoverClusters: async (vaultPathId: string, region?: string): Promise<EKSClusterInfo[]> => {
    const { data } = await configApi.post<{ success: boolean; data: EKSClusterInfo[] }>('/eks/discover', {
      vaultPathId,
      region: region || 'us-east-1'
    });
    return data.data;
  },

  /**
   * Generate kubeconfig for an EKS cluster
   */
  generateKubeconfig: async (request: EKSKubeconfigRequest): Promise<string> => {
    const { data } = await configApi.post<{ success: boolean; data: { kubeconfigPath: string } }>('/eks/generate-kubeconfig', request);
    return data.data.kubeconfigPath;
  },
};

// ============================================================================
// Keycloak API
// ============================================================================

export const keycloakApi = {
  /**
   * Get all Keycloak instances
   */
  getAllInstances: async (): Promise<KeycloakConfig[]> => {
    const { data } = await configApi.get<KeycloakConfig[]>('/keycloaks');
    return data;
  },

  /**
   * Get specific Keycloak instance
   */
  getInstance: async (instanceId: string): Promise<KeycloakConfig> => {
    const { data } = await configApi.get<KeycloakConfig>(`/keycloaks/${instanceId}`);
    return data;
  },

  /**
   * Delete Keycloak instance
   */
  deleteInstance: async (instanceId: string): Promise<void> => {
    await configApi.delete(`/keycloaks/${instanceId}`);
  },

  /**
   * Update Keycloak instance
   */
  updateInstance: async (
    instanceId: string,
    updates: Partial<Omit<KeycloakConfig, 'id' | 'accessToken' | 'refreshToken' | 'tokenExpiration' | 'lastAuthenticated'>>
  ): Promise<KeycloakConfig> => {
    const { data } = await configApi.put<{ success: boolean; data: KeycloakConfig }>(`/keycloaks/${instanceId}`, updates);
    return data.data;
  },

  /**
   * Authenticate with Keycloak
   */
  authenticate: async (request: KeycloakAuthRequest): Promise<KeycloakConfig> => {
    const { data } = await configApi.post<{ success: boolean; data: KeycloakConfig }>('/keycloak/auth', request);
    return data.data;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (instanceId: string): Promise<void> => {
    await configApi.post(`/keycloak/${instanceId}/refresh`);
  },

  /**
   * Get valid access token (auto-refreshes if needed)
   */
  getAccessToken: async (instanceId: string): Promise<string> => {
    const { data } = await configApi.get<{ success: boolean; token: string }>(`/keycloak/${instanceId}/token`);
    return data.token;
  },

  /**
   * Introspect token to check validity
   */
  introspectToken: async (instanceId: string): Promise<KeycloakIntrospectResponse> => {
    const { data } = await configApi.post<{ success: boolean; data: KeycloakIntrospectResponse }>(`/keycloak/${instanceId}/introspect`);
    return data.data;
  },

  /**
   * Test connection to Keycloak
   */
  testConnection: async (url: string, realm: string): Promise<TestConnectionResponse> => {
    const { data } = await configApi.post<TestConnectionResponse>('/keycloak/test', { url, realm });
    return data;
  },

  /**
   * Get OAuth authorization URL for authorization code flow
   */
  getAuthUrl: async (params: {
    url: string;
    realm: string;
    clientId: string;
    redirectUri: string;
    scopes?: string[];
    state?: string;
  }): Promise<string> => {
    const { data } = await configApi.post<{ success: boolean; authUrl: string }>('/keycloak/auth-url', params);
    return data.authUrl;
  },
};

// ============================================================================
// RangeOS API
// ============================================================================

export const rangeosApi = {
  /**
   * Get all RangeOS environments
   */
  getAllEnvironments: async (): Promise<RangeOSEnvironment[]> => {
    const { data } = await configApi.get<RangeOSEnvironment[]>('/rangeos');
    return data;
  },

  /**
   * Get specific RangeOS environment
   */
  getEnvironment: async (environmentId: string): Promise<RangeOSEnvironment> => {
    const { data } = await configApi.get<RangeOSEnvironment>(`/rangeos/${environmentId}`);
    return data;
  },

  /**
   * Create RangeOS environment
   */
  createEnvironment: async (environment: Omit<RangeOSEnvironment, 'id' | 'createdAt' | 'updatedAt'>): Promise<RangeOSEnvironment> => {
    const { data } = await configApi.post<{ success: boolean; data: RangeOSEnvironment }>('/rangeos', environment);
    return data.data;
  },

  /**
   * Update RangeOS environment
   */
  updateEnvironment: async (environmentId: string, updates: Partial<Omit<RangeOSEnvironment, 'id' | 'createdAt' | 'updatedAt'>>): Promise<RangeOSEnvironment> => {
    const { data } = await configApi.put<{ success: boolean; data: RangeOSEnvironment }>(`/rangeos/${environmentId}`, updates);
    return data.data;
  },

  /**
   * Delete RangeOS environment
   */
  deleteEnvironment: async (environmentId: string): Promise<void> => {
    await configApi.delete(`/rangeos/${environmentId}`);
  },
};
